import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import useModalEffect from '../../../hooks/useModalEffect';
import CompanySection from './LeadsForm/CompanySection';
import ProjectSection from './LeadsForm/ProjectSection';
import ServiceSection from './LeadsForm/ServiceSection';
import api from '../../../api';

/**
 * The blank form, in one place.
 *
 * Used both as RHF's defaultValues and as the reset applied every time the modal opens in
 * create mode — the second is what stops the previous lead's values bleeding into the next
 * one. Keep the two in step by construction rather than by memory.
 */
const EMPTY_LEAD_FORM = {
  company: '',
  contact: '',
  contactPhoneNumber: '',
  email: '',
  address: '',
  companyPhoneNumber: '',
  companyWebsite: '',
  sector: '',
  stage: '',
  leadSource: '',
  projectName: '',
  projectDescription: '',
  assignedUser: '',
  competitor: [],
  departmentId: ''
};

const LeadsForm = ({
  formData: parentFormData = {},
  formErrors,
  handleInputChange: parentHandleInputChange,
  handleTextChange: parentHandleTextChange,
  handleFormSubmit,
  serviceSections = [],
  addServiceSection,
  deleteServiceSection,
  options = {},
  formCascadeUsers = [],
  formCascadeDepts = [],
  isOpen,
  onClose,
  fetchContactOptions,
  standardsOptionsMap = {},
  onSuccessSubmit,
  isEditMode,
  editingLead,
  initializeServiceSections,
  isIsccService,
  isSubmitting = false,
  lockCompany = false
}) => {
  // ✅ PERFORMANCE: Use React Hook Form for optimized form state management
  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: { ...EMPTY_LEAD_FORM },
    mode: 'onBlur' // Only validate on blur, not on every keystroke
  });

  const [isDataInitialized, setIsDataInitialized] = useState(false);
  const isInitializingRef = useRef(false);
  // Tracks whether we've already synced parentFormData → RHF for the current open session (create mode)
  const createModeSyncedRef = useRef(false);

  useModalEffect(isOpen);

  // ✅ PERFORMANCE: Only watch specific fields needed, not all fields
  const watchedCompany = watch('company');

  // Narrow key: only the selected service IDs, not the full parentFormData object.
  // This prevents servicesOptionsPerSection from recomputing when standards/scope/other fields change.
  const serviceSelectionsKey = serviceSections
    .map(idx => parentFormData[`services-${idx}`] || '')
    .join('|');

  const servicesOptionsPerSection = useMemo(() => {
    if (!Array.isArray(options.servicesOptions) || options.servicesOptions.length === 0) return {};
    const result = {};
    serviceSections.forEach(currentIdx => {
      const selectedElsewhere = serviceSections
        .filter(idx => idx !== currentIdx)
        .map(idx => parentFormData[`services-${idx}`])
        .filter(Boolean);
      result[currentIdx] = options.servicesOptions.filter(service =>
        !selectedElsewhere.includes(service.id) &&
        !selectedElsewhere.includes(String(service.id))
      );
    });
    return result;
  // eslint-disable-next-line
  }, [options.servicesOptions, serviceSections, serviceSelectionsKey]);

  const getAvailableServicesCount = useCallback(() => {
    const allSelected = serviceSections
      .map(idx => parentFormData[`services-${idx}`])
      .filter(Boolean);
    return (Array.isArray(options.servicesOptions) ? options.servicesOptions : []).filter(
      s => !allSelected.includes(s.id) && !allSelected.includes(String(s.id))
    ).length;
  }, [options.servicesOptions, serviceSections, serviceSelectionsKey]);

  // ✅ PERFORMANCE: Handle AppSelect changes (dropdowns) - sync immediately
  const handleSelectChange = useCallback((name, value) => {
    setValue(name, value, { shouldValidate: false }); // Update form state without validation
    
    // ✅ PERFORMANCE: Sync critical dropdowns to parent immediately
    if (parentHandleInputChange) {
      parentHandleInputChange(name, value);
    }
    
    // Handle company change — auto-fill (website/phone/address) and contact fetch are now handled
    // by AppAsyncSelect's onItemSelect callback in CompanySection (handleCompanyItemSelect).
    // Only the contact-clear side-effect stays here so changing company always resets the contact.
    if (name === 'company') {
      setValue('contact', '');
      setValue('contactPhoneNumber', '');
      setValue('email', '');
      if (parentHandleInputChange) {
        parentHandleInputChange('contact', '');
        parentHandleInputChange('contactPhoneNumber', '');
        parentHandleInputChange('email', '');
      }
    }
  }, [setValue, parentHandleInputChange, fetchContactOptions, options.companyOptions]);

  // ✅ PERFORMANCE: Handle text input changes - only update local state, sync on blur
  const handleTextInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setValue(name, value, { shouldValidate: false }); // Update form state without validation
    // Don't sync to parent on every keystroke - only on blur
  }, [setValue]);

  // ✅ PERFORMANCE: Sync to parent on blur (not on every keystroke)
  const handleTextInputBlur = useCallback((e) => {
    const { name, value } = e.target;
    
    if (parentHandleTextChange) {
      parentHandleTextChange(e);
    }
    if (parentHandleInputChange) {
      parentHandleInputChange(name, value);
    }
  }, [parentHandleTextChange, parentHandleInputChange]);


  // ✅ PERBAIKI useEffect UNTUK EDIT MODE - GUNAKAN REF UNTUK MENCEGAH INFINITE LOOP
  useEffect(() => {
    const initializeEditData = async () => {
      if (isOpen && isEditMode && editingLead && !isDataInitialized && !isInitializingRef.current) {
        isInitializingRef.current = true;

        // ✅ FIX: Transform editingLead data to form format
        // Find assigned user by ID, email, or name
        let assignedUserId = '';
        
        // ✅ PERBAIKAN: Mapping berdasarkan field yang tersedia di API response
        if (editingLead.assignedTo?.id) {
          assignedUserId = String(editingLead.assignedTo.id);
        } else if (editingLead.assignedUserId) {
          assignedUserId = String(editingLead.assignedUserId);
        } else if (editingLead.assignedUser) {
          const assignedUserOption = options.assignedUserOptions?.find(
            opt => String(opt.value) === String(editingLead.assignedUser) || 
                   opt.email === editingLead.assignedUser ||
                   opt.label === editingLead.assignedUser ||
                   opt.fullName === editingLead.assignedUser
          );
          assignedUserId = assignedUserOption ? assignedUserOption.value : '';
        } else if (editingLead.assignedTo?.firstName && editingLead.assignedTo?.lastName) {
          const fullName = `${editingLead.assignedTo.firstName} ${editingLead.assignedTo.lastName}`;
          const assignedUserOption = options.assignedUserOptions?.find(
            opt => opt.fullName === fullName ||
                   opt.label === fullName ||
                   (opt.firstName === editingLead.assignedTo.firstName && opt.lastName === editingLead.assignedTo.lastName)
          );
          assignedUserId = assignedUserOption ? assignedUserOption.value : '';
        } else if (editingLead.assignUser) {
          const assignedUserOption = options.assignedUserOptions?.find(
            opt => String(opt.value) === String(editingLead.assignUser) || 
                   opt.label === editingLead.assignUser || 
                   opt.email === editingLead.assignUser ||
                   opt.fullName === editingLead.assignUser ||
                   opt.firstName === editingLead.assignUser ||
                   opt.lastName === editingLead.assignUser ||
                   opt.label.toLowerCase().includes(editingLead.assignUser.toLowerCase()) ||
                   opt.fullName.toLowerCase().includes(editingLead.assignUser.toLowerCase())
          );
          assignedUserId = assignedUserOption ? assignedUserOption.value : '';
        }

        const competitorValue = editingLead.leadCompetitorIds ?? editingLead.competitors ?? editingLead.competitor;
        const competitorArray = Array.isArray(competitorValue)
          ? competitorValue
          : competitorValue
            ? (typeof competitorValue === 'string' && competitorValue.startsWith('[')
              ? (() => { try { return JSON.parse(competitorValue); } catch { return [competitorValue]; } })()
              : [competitorValue])
            : [];

        // Map to string IDs for AppSelect (handles UUID strings, objects with id/value)
        const competitorIds = competitorArray.map((c) => {
          if (typeof c === 'object' && c != null) {
            if (c.value != null) return String(c.value);
            if (c.id != null) return String(c.id);
          }
          return String(c);
        }).filter(Boolean);

        const transformedData = {
          company: String(editingLead.companyId || editingLead.company || ''),
          companyWebsite: editingLead.companyWebsite || editingLead.website || '',
          companyPhoneNumber: editingLead.companyPhoneNumber || '',
          address: editingLead.address || '',
          sector: editingLead.sector || editingLead.partnerType || '',
          contact: String(editingLead.contactId || editingLead.contact || ''),
          contactPhoneNumber: editingLead.contactNumber || editingLead.phone || '',
          email: editingLead.email || '',
          stage: String(editingLead.stageId || editingLead.stage || ''),
          projectName: editingLead.projectName || '',
          projectDescription: editingLead.projectDescription || editingLead.description || '',
          leadSource: String(editingLead.sourceId || editingLead.leadSource || ''),
          assignedUser: String(assignedUserId),
          competitor: competitorIds,
          departmentId: String(editingLead.departmentId || editingLead.department?.id || '')
        };

        // Reset form with transformed data
        reset(transformedData);
        
        // Update parent form data with the transformed data
        if (parentHandleInputChange) {
          Object.keys(transformedData).forEach(key => {
            parentHandleInputChange(key, transformedData[key]);
          });
        }

        // Fetch contacts if company is selected
        if (editingLead.companyId || editingLead.company) {
          const companyId = String(editingLead.companyId || editingLead.company);
          await fetchContactOptions(companyId);
        }

        // Initialize service sections if needed
        if (editingLead.services && Array.isArray(editingLead.services)) {
          const services = editingLead.services.map(service => {
            const serviceId = service.serviceId || service.id;

            // standards is [{standardId, scope}, ...] from new backend, or [Long] from legacy
            const standardsRaw = service.standards || service.standardId || [];
            const isPerRowShape = Array.isArray(standardsRaw) && standardsRaw.length > 0
              && standardsRaw[0] !== null && typeof standardsRaw[0] === 'object' && 'standardId' in standardsRaw[0];

            const standardIds = isPerRowShape
              ? standardsRaw.map(s => String(s.standardId)).filter(Boolean)
              : (Array.isArray(standardsRaw) ? standardsRaw : [standardsRaw]).map(s => String(s)).filter(Boolean);

            // Per-row scope: use new shape directly, or fall back to legacy single scope on first row
            let scopeRows;
            if (isPerRowShape) {
              const isIsccHint = standardsRaw.some(s => Array.isArray(s.scope));
              scopeRows = standardsRaw.map(s => {
                const raw = s.scope;
                if (!raw) return isIsccHint ? [] : '';
                if (typeof raw === 'string' && raw.startsWith('[')) {
                  try { return JSON.parse(raw); } catch (e) { return raw; }
                }
                return raw;
              });
              if (scopeRows.length === 0) scopeRows = [''];
            } else {
              const scope = service.scope || '';
              const isIsccOrSustainability = (options.servicesOptions || []).find(opt =>
                String(opt.id) === String(serviceId) || String(opt.value) === String(serviceId)
              )?.name?.toLowerCase().match(/iscc|sustainability/);
              let formattedScope = scope;
              if (isIsccOrSustainability) {
                if (Array.isArray(scope)) formattedScope = scope;
                else if (typeof scope === 'string' && scope.startsWith('[')) {
                  try { formattedScope = JSON.parse(scope); } catch (e) { formattedScope = scope ? [scope] : []; }
                } else formattedScope = scope ? [scope] : [];
              } else {
                if (Array.isArray(scope)) formattedScope = scope.join(', ');
                else if (typeof scope === 'string' && scope.startsWith('[')) {
                  try { const p = JSON.parse(scope); formattedScope = Array.isArray(p) ? p.join(', ') : p; } catch (e) { formattedScope = scope; }
                }
              }
              const rowCount = Math.max(standardIds.length, 1);
              scopeRows = Array.from({ length: rowCount }, (_, rowIdx) =>
                rowIdx !== 0 ? (Array.isArray(formattedScope) ? [] : '') : formattedScope
              );
            }

            return { serviceId, standards: standardIds, scopeRows };
          });
          
          // Initialize service sections first
          await initializeServiceSections(services);
          
          // Then update the form data with all service section values
          services.forEach((service, index) => {
            // Set service
            if (service.serviceId) {
              setValue(`services-${index}`, service.serviceId, { shouldValidate: false });
              if (parentHandleInputChange) {
                parentHandleInputChange(`services-${index}`, service.serviceId);
              }
            }
            
            // Set standards
            if (service.standards && service.standards.length > 0) {
              setValue(`standards-${index}`, service.standards, { shouldValidate: false });
              if (parentHandleInputChange) {
                parentHandleInputChange(`standards-${index}`, service.standards);
              }
            }

            // Set scope — one entry per standard row (already built as scopeRows)
            const scopeRows = service.scopeRows || [''];
            setValue(`scope-${index}`, scopeRows, { shouldValidate: false });
            if (parentHandleInputChange) {
              parentHandleInputChange(`scope-${index}`, scopeRows);
            }
          });
        }

        setIsDataInitialized(true);
        isInitializingRef.current = false;
      }
    };

    initializeEditData();
  }, [isOpen, isEditMode, editingLead?.id, reset, setValue, fetchContactOptions, initializeServiceSections, options.assignedUserOptions, options.servicesOptions, parentHandleInputChange]);

  // Reset flags when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsDataInitialized(false);
      isInitializingRef.current = false;
      createModeSyncedRef.current = false;
    }
  }, [isOpen]);

  // Sync parentFormData → RHF when modal first opens in create mode (once per open session).
  // Using a ref guard so this does NOT re-run on every parentFormData reference change.
  useEffect(() => {
    if (isOpen && !isEditMode && parentFormData && !createModeSyncedRef.current) {
      createModeSyncedRef.current = true;

      // Blank the form first. Without this the modal reopens carrying whatever was typed
      // last time: the sync below only writes fields PRESENT in parentFormData, so any
      // field the parent does not track keeps its previous value. The reset that was
      // supposed to handle this lived in a `hidden.bs.modal` listener, which never fires —
      // these modals are React-controlled and no Bootstrap Modal instance exists to emit
      // that event.
      reset({ ...EMPTY_LEAD_FORM });

      const fieldsToSync = [
        'company', 'contact', 'contactPhoneNumber', 'email', 'address',
        'companyPhoneNumber', 'companyWebsite', 'sector', 'stage',
        'projectName', 'projectDescription', 'leadSource', 'assignedUser', 'competitor'
      ];
      fieldsToSync.forEach(field => {
        if (parentFormData[field] !== undefined && parentFormData[field] !== null) {
          setValue(field, parentFormData[field], { shouldValidate: false });
        }
      });

      if (serviceSections && serviceSections.length > 0) {
        serviceSections.forEach(sectionIndex => {
          const serviceKey = `services-${sectionIndex}`;
          const standardsKey = `standards-${sectionIndex}`;
          const scopeKey = `scope-${sectionIndex}`;
          if (parentFormData[serviceKey] !== undefined) setValue(serviceKey, parentFormData[serviceKey], { shouldValidate: false });
          if (parentFormData[standardsKey] !== undefined) setValue(standardsKey, parentFormData[standardsKey], { shouldValidate: false });
          if (parentFormData[scopeKey] !== undefined) setValue(scopeKey, parentFormData[scopeKey], { shouldValidate: false });
        });
      }
    }
  // eslint-disable-next-line
  }, [isOpen, isEditMode]);

  // Add useEffect to handle contact after options are loaded
  useEffect(() => {
    if (
      isEditMode &&
      editingLead &&
      watchedCompany &&
      options.contactOptions &&
      options.contactOptions.length > 0 &&
      !watch('contact')
    ) {
      // Cek apakah contact dari editingLead ada di contactOptions
      const contactId = String(editingLead.contact || editingLead.pic || '');
      const found = options.contactOptions.find(opt => String(opt.value) === contactId);
      if (found) {
        setValue('contact', contactId, { shouldValidate: false });
        setValue('contactPhoneNumber', found.contactNumber || '', { shouldValidate: false });
        setValue('email', found.email || '', { shouldValidate: false });
        if (parentHandleInputChange) {
          parentHandleInputChange('contact', contactId);
          parentHandleInputChange('contactPhoneNumber', found.contactNumber || '');
          parentHandleInputChange('email', found.email || '');
        }
      }
    }
  }, [isEditMode, editingLead, watchedCompany, options.contactOptions, watch, setValue, parentHandleInputChange]);

  // The reset that used to live here hung off a `hidden.bs.modal` listener. These modals
  // are React-controlled (no Bootstrap Modal instance exists, and nothing uses
  // data-bs-toggle), so that event never fired and the reset never ran — which is why the
  // form reopened holding the previous lead's input. It now runs from the open effect
  // above, where every other form in this app does its reset.

  const handleClose = () => {
    
    
    if (onClose) onClose();
  };

  // Determine modal title based on mode
  const getModalTitle = () => {
    if (isEditMode && editingLead) {
      return 'Edit Lead';
    }
    return 'Create Lead';
  };

  return (
    <>
      <style>{`
        #modal-leads .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-leads .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-leads .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-leads .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-leads .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-leads .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`}
        id="modal-leads"
        style={{ display: isOpen ? 'block' : 'none' }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{getModalTitle()}</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
                disabled={isSubmitting}
              ></button>
            </div>
            <form onSubmit={handleSubmit((data) => {
              // Sync all form data to parent before submit
              if (parentHandleInputChange) {
                Object.keys(data).forEach(key => {
                  if (data[key] !== undefined && data[key] !== null) {
                    parentHandleInputChange(key, data[key]);
                  }
                });
                // Also sync service sections data
                serviceSections.forEach(sectionIndex => {
                  const serviceValue = data[`services-${sectionIndex}`] || parentFormData[`services-${sectionIndex}`] || '';
                  const standardsValue = data[`standards-${sectionIndex}`] || parentFormData[`standards-${sectionIndex}`] || [];
                  const scopeValue = data[`scope-${sectionIndex}`] || parentFormData[`scope-${sectionIndex}`] || '';
                  if (serviceValue) parentHandleInputChange(`services-${sectionIndex}`, serviceValue);
                  if (standardsValue && standardsValue.length > 0) parentHandleInputChange(`standards-${sectionIndex}`, standardsValue);
                  if (scopeValue) parentHandleInputChange(`scope-${sectionIndex}`, scopeValue);
                });
              }
              
              // Merge with parent form data - React Hook Form data takes priority
              const dataToSubmit = {
                ...parentFormData,
                ...data,
                // Ensure service sections data is included (from data first, then parentFormData)
                ...serviceSections.reduce((acc, sectionIndex) => {
                  acc[`services-${sectionIndex}`] = data[`services-${sectionIndex}`] || parentFormData[`services-${sectionIndex}`] || '';
                  acc[`standards-${sectionIndex}`] = data[`standards-${sectionIndex}`] || parentFormData[`standards-${sectionIndex}`] || [];
                  acc[`scope-${sectionIndex}`] = data[`scope-${sectionIndex}`] || parentFormData[`scope-${sectionIndex}`] || '';
                  return acc;
                }, {})
              };
              
              // Create a synthetic event for compatibility
              const syntheticEvent = {
                preventDefault: () => {},
                target: dataToSubmit
              };
              
              handleFormSubmit(syntheticEvent, () => {
                handleClose();
                if (onSuccessSubmit) {
                  onSuccessSubmit();
                }
              }, editingLead);
            })}>
              <div className="modal-body">
                    <CompanySection
                      control={control}
                      setValue={setValue}
                      watch={watch}
                      formErrors={formErrors}
                      errors={errors}
                      handleSelectChange={handleSelectChange}
                      handleTextInputChange={handleTextInputChange}
                      handleTextInputBlur={handleTextInputBlur}
                      options={options}
                      fetchContactOptions={fetchContactOptions}
                      lockCompany={lockCompany}
                    />
                    <ProjectSection
                      control={control}
                      watch={watch}
                      formErrors={formErrors}
                      errors={errors}
                      handleSelectChange={handleSelectChange}
                      handleTextInputChange={handleTextInputChange}
                      handleTextInputBlur={handleTextInputBlur}
                      options={options}
                      formCascadeUsers={formCascadeUsers}
                      formCascadeDepts={formCascadeDepts}
                    />
                    <div className="inv-section-label">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                      Services
                    </div>
                    {serviceSections.map((sectionIndex) => (
                      <ServiceSection
                        key={sectionIndex}
                        index={sectionIndex}
                        onDelete={deleteServiceSection}
                        control={control}
                        watch={watch}
                        setValue={setValue}
                        handleSelectChange={handleSelectChange}
                        handleTextInputChange={handleTextInputChange}
                        handleTextInputBlur={handleTextInputBlur}
                        servicesOptions={servicesOptionsPerSection[sectionIndex] || []}
                        standardsOptions={standardsOptionsMap[sectionIndex] || []}
                        formErrors={formErrors}
                        errors={errors}
                        isIsccService={isIsccService}
                      />
                    ))}
                    <div>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={(e) => {
                          e.preventDefault(); // ✅ Prevent form submission
                          e.stopPropagation(); // ✅ Stop event bubbling
                          addServiceSection();
                        }}
                        disabled={getAvailableServicesCount() === 0}
                        title={getAvailableServicesCount() === 0 ? "All services are already selected" : `${getAvailableServicesCount()} service(s) available to add`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                          viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          className="icon icon-2">
                          <path d="M12 5l0 14"></path>
                          <path d="M5 12l14 0"></path>
                        </svg>
                        Add Services
                      </button>
                    </div>
              </div>
              <div className="inv-footer">
                <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                  {isSubmitting ? (isEditMode ? 'Updating lead…' : 'Saving lead…') : (
                    <span style={{ color: '#9aa5b1' }}>
                      <span className="inv-required-star">*</span> Required fields
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-inv-save"
                  id="createBtn-leads"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 14, height: 14, borderWidth: 2 }} />
                      {isEditMode ? 'Updating…' : 'Saving…'}
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      {isEditMode ? 'Update Lead' : 'Create Lead'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadsForm;
